import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Download, History, Loader2, RefreshCw, Send, Sparkles } from 'lucide-react';
import {
  askInsights,
  listInsightVersions,
  type AskResult,
  type InsightVersion,
} from '@/lib/admin/insights-api';
import { toast } from '@/hooks/use-toast';
import { AskResultView } from './AskResultView';
import { downloadAskPdf } from './askPdf';
import { AskProgress } from './AskProgress';
import { ErrorNotice } from '@/components/common/ErrorNotice';
import { friendlyError } from '@/lib/errors/friendly';

type Scope = { orgId: string; surveyId?: string; initialQuestion?: string };

const SURVEY_PROMPTS = [
  'Which anchor has the biggest gap from its target?',
  'Which anchors have declined most in the last month?',
  'Are there any bands where the scores are worse than average?',
  'What themes are coming up in conversations related to this survey?',
];

const ORG_PROMPTS = [
  'How is overall pulse trending this quarter?',
  'Which team has the lowest pulse mean right now?',
  'What themes came up most in Charles conversations this month?',
  'Where should leadership focus attention next week?',
];

export function AskInsightsPanel({ scope }: { scope: Scope }) {
  const qc = useQueryClient();
  const [question, setQuestion] = useState(scope.initialQuestion ?? '');
  const [askedQuestion, setAskedQuestion] = useState('');
  const [result, setResult] = useState<AskResult | null>(null);
  const [askError, setAskError] = useState<unknown>(null);
  const [downloading, setDownloading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const versionsKey = ['insight-versions', scope.orgId, scope.surveyId ?? 'org'];
  const versions = useQuery({
    queryKey: versionsKey,
    queryFn: () => listInsightVersions({ orgId: scope.orgId, surveyId: scope.surveyId }),
  });

  const mut = useMutation({
    mutationFn: (q: string) =>
      askInsights({ orgId: scope.orgId, surveyId: scope.surveyId, question: q }),
    onSuccess: (data) => {
      setAskError(null);
      setResult(data);
      void qc.invalidateQueries({ queryKey: versionsKey });
    },
    onError: (e: Error) => setAskError(e),
  });

  const prompts = scope.surveyId ? SURVEY_PROMPTS : ORG_PROMPTS;

  const submit = (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 3) return;
    setAskedQuestion(trimmed);
    setQuestion(trimmed);
    mut.mutate(trimmed);
  };

  const openVersion = (v: InsightVersion) => {
    setAskedQuestion(v.question);
    setQuestion(v.question);
    setResult(v.payload);
    setAskError(null);
  };

  const download = async (r: AskResult, q: string) => {
    setDownloading(true);
    try {
      await downloadAskPdf({
        result: r,
        question: q,
        filenameSlug: `ask-${q}`,
        scopeLabel: scope.surveyId ? 'Survey Ask brief' : 'Organisation Ask brief',
      });
    } catch (e) {
      toast({
        title: 'Could not generate PDF',
        description: friendlyError(e, 'Could not generate PDF').description,
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <CardTitle className="text-base font-heading">Ask</CardTitle>
        </div>
        <CardDescription>
          Ask a plain-English question. Answers use only k≥5 aggregated signals — never raw responses.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {prompts.map((p) => (
            <Button
              key={p}
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-auto py-1.5"
              onClick={() => submit(p)}
              disabled={mut.isPending}
            >
              {p}
            </Button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(question);
          }}
          className="space-y-2"
        >
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Which team has trended down most this month?"
            rows={2}
            maxLength={500}
            disabled={mut.isPending}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{question.length}/500</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory((s) => !s)}
              >
                <History className="h-4 w-4 mr-2" />
                Saved versions {versions.data ? `(${versions.data.length})` : ''}
              </Button>
              <Button type="submit" size="sm" disabled={mut.isPending || question.trim().length < 3}>
                {mut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Ask
              </Button>
            </div>
          </div>
        </form>

        {showHistory && (
          <div className="rounded-md border border-border divide-y divide-border">
            {versions.isLoading && (
              <p className="p-3 text-xs text-muted-foreground">Loading saved versions…</p>
            )}
            {versions.data?.length === 0 && (
              <p className="p-3 text-xs text-muted-foreground">
                No saved versions yet. Every answer you generate is kept here.
              </p>
            )}
            {(versions.data ?? []).map((v) => (
              <div key={v.id} className="p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{v.question}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {new Date(v.created_at).toLocaleString()}
                    {v.headline ? ` · ${v.headline}` : ''}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button type="button" variant="ghost" size="sm" onClick={() => openVersion(v)}>
                    Open
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={downloading}
                    onClick={() => download(v.payload, v.question)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {mut.isPending && <AskProgress />}

        {askError != null && !mut.isPending && (
          <ErrorNotice
            error={askError}
            title="We could not finish that answer"
            onRetry={() => submit(askedQuestion || question)}
            retryLabel="Ask again"
          />
        )}

        {result && (
          <div className="space-y-2">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => submit(askedQuestion)}
                disabled={mut.isPending || askedQuestion.trim().length < 3}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate insight
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => download(result, askedQuestion)}
                disabled={downloading}
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download PDF
              </Button>
            </div>
            <AskResultView ref={reportRef} question={askedQuestion} result={result} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
